import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  Pencil,
  Trash2,
  Check,
  X,
  Ban,
  AlertCircle,
} from 'lucide-react';
import { LaborConfirmDialog } from '../../calendario/components/LaborConfirmDialog';
import { laborAbsenceService } from '../services/laborAbsenceService';
import {
  ABSENCE_TYPES,
  ABSENCE_STATUSES,
  countAbsenceDays,
  formatAbsenceDate,
  getAbsenceEmployeeName,
  getAbsenceTypeLabel,
} from '../utils/laborAbsenceUtils';

const ACTION_BTN_BASE =
  'inline-flex items-center justify-center p-2 rounded-lg border cursor-pointer transition-all duration-150 active:scale-95 focus:outline-none focus-visible:ring-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';

function TypeBadge({ type }) {
  const config = ABSENCE_TYPES[type];
  if (!config) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-700 text-gray-400 border border-gray-600">
        {type || '—'}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const config = ABSENCE_STATUSES[status];
  if (!config) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-700 text-gray-400 border border-gray-600">
        {status || '—'}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

export function LaborAbsenceTable({
  absences = [],
  isLoading = false,
  isFetching = false,
  employeeNameById = {},
  approverEmployeeId = null,
  onEdit,
  onActionError,
  readOnly = false,
  approvalOnly = false,
}) {
  const queryClient = useQueryClient();
  const [confirmState, setConfirmState] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['labor-absences'] });
  };

  const approveMutation = useMutation({
    mutationFn: ({ id, approvedBy }) => laborAbsenceService.approveAbsence(id, approvedBy),
    onSuccess: async () => {
      setConfirmState(null);
      await invalidate();
    },
    onError: (err) => onActionError?.(err?.message || 'No se pudo aprobar'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => laborAbsenceService.rejectAbsence(id, reason),
    onSuccess: async () => {
      setConfirmState(null);
      setRejectReason('');
      await invalidate();
    },
    onError: (err) => onActionError?.(err?.message || 'No se pudo rechazar'),
  });

  const cancelMutation = useMutation({
    mutationFn: (id) => laborAbsenceService.cancelAbsence(id),
    onSuccess: async () => {
      setConfirmState(null);
      await invalidate();
    },
    onError: (err) => onActionError?.(err?.message || 'No se pudo cancelar'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => laborAbsenceService.deleteAbsence(id),
    onSuccess: async () => {
      setConfirmState(null);
      await invalidate();
    },
    onError: (err) => onActionError?.(err?.message || 'No se pudo eliminar'),
  });

  const isPending =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    cancelMutation.isPending ||
    deleteMutation.isPending;

  const handleConfirm = () => {
    if (!confirmState) return;
    const { action, absence } = confirmState;

    if (action === 'approve') {
      if (!approverEmployeeId) {
        onActionError?.('Tu usuario no está vinculado a un empleado para aprobar ausencias');
        return;
      }
      approveMutation.mutate({ id: absence.id, approvedBy: approverEmployeeId });
      return;
    }
    if (action === 'reject') {
      rejectMutation.mutate({ id: absence.id, reason: rejectReason });
      return;
    }
    if (action === 'cancel') {
      cancelMutation.mutate(absence.id);
      return;
    }
    if (action === 'delete') {
      deleteMutation.mutate(absence.id);
    }
  };

  const openConfirm = (action, absence) => {
    setRejectReason('');
    setConfirmState({ action, absence });
  };

  if (isLoading) {
    return (
      <p className="text-gray-500 text-sm flex items-center gap-2 py-10 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" />
        Cargando ausencias…
      </p>
    );
  }

  if (absences.length === 0) {
    return (
      <p className="text-amber-400 text-sm bg-amber-900/20 border border-amber-700/50 rounded-lg p-6 text-center">
        No hay ausencias registradas con los filtros actuales.
      </p>
    );
  }

  return (
    <>
      {isFetching && !isLoading && (
        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          Actualizando…
        </p>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-900/80 text-gray-400">
            <tr>
              <th className="px-3 py-2 font-medium">Empleado</th>
              <th className="px-3 py-2 font-medium">Tipo</th>
              <th className="px-3 py-2 font-medium">Desde</th>
              <th className="px-3 py-2 font-medium">Hasta</th>
              <th className="px-3 py-2 font-medium text-center">Días</th>
              <th className="px-3 py-2 font-medium">Estado</th>
              <th className="px-3 py-2 font-medium">Motivo</th>
              {!readOnly && <th className="px-3 py-2 font-medium w-36">Acciones</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {absences.map((absence) => {
              const days = countAbsenceDays(absence.start_date, absence.end_date);
              const employeeName = getAbsenceEmployeeName(absence, employeeNameById);

              return (
                <tr key={absence.id} className="hover:bg-gray-800/40">
                  <td className="px-3 py-2 text-gray-200 whitespace-nowrap">{employeeName}</td>
                  <td className="px-3 py-2">
                    <TypeBadge type={absence.absence_type} />
                  </td>
                  <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
                    {formatAbsenceDate(absence.start_date)}
                  </td>
                  <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
                    {formatAbsenceDate(absence.end_date)}
                  </td>
                  <td className="px-3 py-2 text-gray-300 text-center">{days}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={absence.status} />
                  </td>
                  <td
                    className="px-3 py-2 text-gray-400 max-w-[180px] truncate"
                    title={absence.reason || ''}
                  >
                    {absence.reason || '—'}
                  </td>
                  {!readOnly && (
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {absence.status === 'pending' && (
                          <>
                            {!approvalOnly && (
                              <button
                                type="button"
                                onClick={() => onEdit?.(absence)}
                                className={`${ACTION_BTN_BASE} border-gray-600 text-gray-400 hover:text-white hover:bg-gray-700`}
                                title="Editar"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => openConfirm('approve', absence)}
                              disabled={isPending}
                              className={`${ACTION_BTN_BASE} border-emerald-700 text-emerald-400 hover:bg-emerald-900/40`}
                              title="Aprobar"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openConfirm('reject', absence)}
                              disabled={isPending}
                              className={`${ACTION_BTN_BASE} border-red-700 text-red-400 hover:bg-red-900/40`}
                              title="Rechazar"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            {!approvalOnly && (
                              <button
                                type="button"
                                onClick={() => openConfirm('delete', absence)}
                                disabled={isPending}
                                className={`${ACTION_BTN_BASE} border-gray-600 text-gray-400 hover:text-red-300 hover:bg-red-900/30`}
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        )}
                        {absence.status === 'approved' && !approvalOnly && (
                          <button
                            type="button"
                            onClick={() => openConfirm('cancel', absence)}
                            disabled={isPending}
                            className={`${ACTION_BTN_BASE} border-amber-700 text-amber-400 hover:bg-amber-900/40`}
                            title="Cancelar ausencia"
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                        {absence.status === 'rejected' && !approvalOnly && (
                          <button
                            type="button"
                            onClick={() => openConfirm('delete', absence)}
                            disabled={isPending}
                            className={`${ACTION_BTN_BASE} border-gray-600 text-gray-400 hover:text-red-300 hover:bg-red-900/30`}
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <LaborConfirmDialog
        isOpen={Boolean(confirmState)}
        onClose={() => !isPending && setConfirmState(null)}
        onConfirm={handleConfirm}
        title={
          confirmState?.action === 'approve'
            ? 'Aprobar ausencia'
            : confirmState?.action === 'reject'
              ? 'Rechazar ausencia'
              : confirmState?.action === 'cancel'
                ? 'Cancelar ausencia'
                : 'Eliminar ausencia'
        }
        message={
          confirmState?.absence
            ? `${getAbsenceEmployeeName(confirmState.absence, employeeNameById)} · ${getAbsenceTypeLabel(confirmState.absence.absence_type)} · ${formatAbsenceDate(confirmState.absence.start_date)} → ${formatAbsenceDate(confirmState.absence.end_date)}`
            : ''
        }
        confirmLabel={
          confirmState?.action === 'approve'
            ? 'Aprobar'
            : confirmState?.action === 'reject'
              ? 'Rechazar'
              : confirmState?.action === 'cancel'
                ? 'Cancelar ausencia'
                : 'Eliminar'
        }
        cancelLabel="Cerrar"
        variant={confirmState?.action === 'approve' ? 'primary' : 'danger'}
        isPending={isPending}
      >
        {confirmState?.action === 'approve' && !approverEmployeeId && (
          <p className="mt-3 text-sm text-amber-300 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            Tu usuario no está vinculado a un empleado. No se puede registrar la aprobación.
          </p>
        )}
        {confirmState?.action === 'reject' && (
          <div className="mt-3">
            <label htmlFor="reject-reason" className="block text-xs text-gray-400 mb-1">
              Motivo del rechazo (opcional)
            </label>
            <textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={2}
              className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm resize-y"
              placeholder="Indica el motivo…"
            />
          </div>
        )}
        {confirmState?.action === 'cancel' && (
          <p className="mt-3 text-sm text-gray-400">
            La ausencia dejará de contar como aprobada y el empleado volverá a estar disponible en esas fechas.
          </p>
        )}
        {confirmState?.action === 'delete' && (
          <p className="mt-3 text-sm text-gray-400">Esta acción no se puede deshacer.</p>
        )}
      </LaborConfirmDialog>
    </>
  );
}
